import axios from 'axios';
import { Product, PickupLocation, Order, LoginCredentials, AuthResponse, Faq } from '../types';
import { getApiUrl } from '../config/config';

// Define image upload response type
export interface ImageUploadResponse {
  message: string;
  imageUrl: string;
  key: string;
}

// Create axios instance with dynamic base URL
const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Set base URL dynamically from config
api.interceptors.request.use((config) => {
  config.baseURL = getApiUrl();
  return config;
});

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if error response status is 401 (Unauthorized)
    if (error.response && error.response.status === 401) {
      // Clear auth data from localStorage
      localStorage.removeItem('adminUser');
      localStorage.removeItem('adminToken');
      
      // Navigate to login page
      window.location.href = '/admin/login';
    }
    
    // Reject the promise to allow error handling in components
    return Promise.reject(error);
  }
);

// Product API calls
export const getProducts = async (): Promise<Product[]> => {
  const response = await api.get('/products');
  return response.data;
};

export const getProductById = async (id: string): Promise<Product> => {
  const response = await api.get(`/products/${id}`);
  return response.data;
};

// Pickup Location API calls
export const getPickupLocations = async (): Promise<PickupLocation[]> => {
  const response = await api.get('/pickup-locations');
  return response.data;
};

export const getPickupLocationById = async (id: string): Promise<PickupLocation> => {
  const response = await api.get(`/pickup-locations/${id}`);
  return response.data;
};

// Order API calls
export const createOrder = async (orderData: Omit<Order, '_id'>): Promise<Order> => {  
  // Transform the data to ensure we're using the correct property name
  const transformedData = { ...orderData };
  
  // If PickupLocationId exists (from old code), convert it to pickupLocationId
  if ('PickupLocationId' in transformedData) {    
    // Use type assertion to fix TypeScript error
    const pickupLocationId = (transformedData as any).PickupLocationId;
    transformedData.pickupLocationId = pickupLocationId;
    delete (transformedData as any).PickupLocationId;
  }
  
  // Ensure pickupLocationId is set and is a valid number
  if (transformedData.pickupLocationId === undefined || 
      transformedData.pickupLocationId === null || 
      isNaN(transformedData.pickupLocationId as number)) {
    
    transformedData.pickupLocationId = 1; // Set a default value as a fallback
  } else {
    // Ensure it's a number, not a string
    transformedData.pickupLocationId = Number(transformedData.pickupLocationId);
  }
    
  const response = await api.post('/orders', transformedData);
  
  return response.data;
};

export const getOrderById = async (id: string): Promise<Order> => {
  const response = await api.get(`/orders/${id}`);
  return response.data;
};

// Admin API calls
export const loginAdmin = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  const response = await api.post('/admin/login', credentials);
  return response.data;
};

// Admin Profile API calls
export interface AdminProfile {
  id: number;
  email: string;
  username: string;
  name: string;
}

export const getAdminProfile = async (token: string): Promise<AdminProfile> => {
  const response = await api.get('/admin/profile', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateAdminProfile = async (profileData: { email: string; name: string }, token: string): Promise<AdminProfile> => {
  const response = await api.put('/admin/profile', profileData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const changeAdminPassword = async (passwordData: { oldPassword: string; newPassword: string }, token: string): Promise<{ message: string }> => {
  const response = await api.put('/admin/change-password', passwordData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Admin User Management API calls
export interface AdminUser {
  id: number;
  email: string;
  username: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export const adminApi = {
  getAllAdmins: async (token: string): Promise<AdminUser[]> => {
    const response = await api.get('/admin/users', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  
  createAdmin: async (adminData: { email: string; username: string; name: string; password: string }, token: string): Promise<AdminUser> => {
    const response = await api.post('/admin/users', adminData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }
};

// Admin Product API calls
export const getAdminProducts = async (token: string): Promise<Product[]> => {
  const response = await api.get('/admin/products', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const createProduct = async (productData: Omit<Product, '_id'>, token: string): Promise<Product> => {
  const response = await api.post('/admin/products', productData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateProduct = async (id: string, productData: Partial<Product>, token: string): Promise<Product> => {
  if (!id) {
    throw new Error('Product ID is required for updating');
  }
  
  console.log('API Service: Updating product with ID:', id);
  
  const response = await api.put(`/admin/products/${id}`, productData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const deleteProduct = async (id: string, token: string): Promise<{ message: string }> => {
  if (!id) {
    throw new Error('Product ID is required');
  }
  
  console.log('API Service: Deleting product with ID:', id);
  
  const response = await api.delete(`/admin/products/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Admin Pickup Location API calls
export const getAdminPickupLocations = async (token: string): Promise<PickupLocation[]> => {
  const response = await api.get('/admin/pickup-locations', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const createPickupLocation = async (locationData: Omit<PickupLocation, '_id'>, token: string): Promise<PickupLocation> => {
  const response = await api.post('/admin/pickup-locations', locationData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updatePickupLocation = async (id: string, locationData: Partial<PickupLocation>, token: string): Promise<PickupLocation> => {
  const response = await api.put(`/admin/pickup-locations/${id}`, locationData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const deletePickupLocation = async (id: string, token: string): Promise<{ message: string }> => {
  const response = await api.delete(`/admin/pickup-locations/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Admin Order API calls
export const getAdminOrders = async (token: string): Promise<Record<string, Order[]>> => {
  const response = await api.get('/admin/orders', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getAdminOrdersByDateRange = async (startDate: string, endDate: string, token: string): Promise<Record<string, Order[]>> => {
  const response = await api.get(`/admin/orders/by-date`, {
    params: { startDate, endDate },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getAdminOrderById = async (id: string, token: string): Promise<Order> => {
  const response = await api.get(`/admin/orders/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateOrderStatus = async (id: string, status: string, token: string): Promise<Order> => {
  const response = await api.put(`/admin/orders/${id}`, { status }, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const deleteOrder = async (id: string, token: string): Promise<{ message: string }> => {
  const response = await api.delete(`/admin/orders/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// FAQ API calls
export const getFaqs = async (): Promise<Faq[]> => {
  const response = await api.get('/faqs');
  return response.data;
};

export const getFaqById = async (id: string): Promise<Faq> => {
  const response = await api.get(`/faqs/${id}`);
  return response.data;
};

// Admin FAQ API calls
export const getAdminFaqs = async (token: string): Promise<Faq[]> => {
  const response = await api.get('/admin/faqs', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getAdminFaqById = async (id: string, token: string): Promise<Faq> => {
  const response = await api.get(`/admin/faqs/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const createFaq = async (faqData: Omit<Faq, 'id' | '_id'>, token: string): Promise<Faq> => {
  const response = await api.post('/admin/faqs', faqData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateFaq = async (id: string, faqData: Partial<Faq>, token: string): Promise<Faq> => {
  if (!id) {
    throw new Error('FAQ ID is required for updating');
  }
  
  console.log('API Service: Updating FAQ with ID:', id);
  
  const response = await api.put(`/admin/faqs/${id}`, faqData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const deleteFaq = async (id: string, token: string): Promise<{ message: string }> => {
  if (!id) {
    throw new Error('FAQ ID is required');
  }
  
  console.log('API Service: Deleting FAQ with ID:', id);
  
  const response = await api.delete(`/admin/faqs/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const reorderFaqs = async (orderData: { id: number | string, order: number }[], token: string): Promise<Faq[]> => {
  const response = await api.put('/admin/faqs/reorder', { orderData }, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Image upload API calls
export const uploadImage = async (imageFile: File, token: string): Promise<ImageUploadResponse> => {
  // Create form data for file upload
  const formData = new FormData();
  formData.append('image', imageFile);
  
  const response = await axios.post(`${getApiUrl()}/admin/upload`, formData, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    },
  });
  
  return response.data;
};

export const deleteImage = async (imageUrl: string, token: string): Promise<{ message: string }> => {
  const response = await api.delete('/admin/upload', {
    headers: { Authorization: `Bearer ${token}` },
    data: { imageUrl }
  });
  
  return response.data;
};

export default api;
